import { FC } from 'react';

import { BreakdownsResult } from 'api/useAnalytics';
import { PieChart } from 'recharts';

import {
  ChartContainer,
  ChartLegend,
  ChartTooltip
} from 'components/ui/chart';

import { AnalyticsBox } from '../AnalyticsBox';
import { PieTooltip } from '../AnalyticsChartTooltip/PieTooltip';
import { AnalyticsLegendWithMore } from '../AnalyticsLegendWithMore/AnalyticsLegendWithMore';
import { AnalyticsLoadingBox } from '../AnalyticsLoadingBox';

import { usePieChartConfig } from './hooks/use-pie-chart-config';

type AnalyticsPieBoxProps = {
  isLoading: boolean;
  breakdown: BreakdownsResult | undefined;
  children: React.ReactElement | React.ReactElement[];
  shouldDisplayLegend?: boolean;
  shouldDisplayLabel?: boolean;
  shouldDisplayValue?: boolean;
};

export const AnalyticsPieBox: FC<AnalyticsPieBoxProps> = ({
  isLoading,
  breakdown,
  children,
  shouldDisplayLegend = true,
  shouldDisplayLabel = true,
  shouldDisplayValue = false
}) => {
  const { chartConfig } = usePieChartConfig({
    breakdown,
    shouldDisplayValue,
    shouldDisplayLabel
  });

  if (isLoading || !breakdown || !chartConfig) return <AnalyticsLoadingBox />;

  const metricName = breakdown.name;

  return (
    <AnalyticsBox metricName={metricName} breakdown={breakdown}>
      <ChartContainer
        config={chartConfig}
        className="w-full h-full"
      >
        <PieChart className='min-h-[320px]' margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
          <ChartTooltip content={<PieTooltip metricType={breakdown.type} />} />

          {children}
          {shouldDisplayLegend && (
            <ChartLegend
              content={<AnalyticsLegendWithMore nameKey="breakdownValue" />}
              verticalAlign="bottom"
            />
          )}
        </PieChart>
      </ChartContainer>
    </AnalyticsBox>
  );
};
